import escapeRegExp from 'lodash/escapeRegExp';

export interface LabelLink {
  href: string;
  text: string;
}

export interface LabelWithLinks {
  /** Label-Text ohne die enthaltenen URLs */
  text: string;
  /** Erkannte URLs in Reihenfolge des Auftretens, ohne Duplikate */
  links: LabelLink[];
}

// http(s)-URLs bis zum nächsten Leerzeichen, Anführungszeichen oder Tag;
// das letzte Zeichen darf kein Satzzeichen sein, damit ein abschließender
// Punkt oder ein Komma im Text bleibt.
const URL_PATTERN = /https?:\/\/[^\s<>"']*[^\s<>"'.,;:!?]/gi;

const countChar = (value: string, char: string): number =>
  value.split(char).length - 1;

// Eine URL in Klammern „(https://…)" endet im Treffer mit der schließenden
// Klammer; Klammern, die innerhalb der URL ausgeglichen sind
// (Wikipedia-Stil), bleiben erhalten.
const trimUnbalancedParentheses = (url: string): string => {
  let result = url;
  while (
    result.endsWith(')') &&
    countChar(result, ')') > countChar(result, '(')
  ) {
    result = result.slice(0, -1);
  }

  return result;
};

// Kodierte Zeichen (z. B. %20) für die Anzeige zurückwandeln; ungültige
// Sequenzen bleiben unverändert.
const decodeForDisplay = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

/**
 * Kurzer, lesbarer Linktext: Dateiname (letztes Pfadsegment mit Endung),
 * sonst der Hostname. Fällt bei ungültigen URLs auf den Eingabewert zurück.
 */
export const linkTextForUrl = (url: string): string => {
  try {
    const { hostname, pathname } = new URL(url);
    const lastSegment = pathname.split('/').filter(Boolean).pop();

    if (lastSegment !== undefined && lastSegment.includes('.')) {
      return decodeForDisplay(lastSegment);
    }

    return hostname;
  } catch {
    return url;
  }
};

/**
 * Trennt in einem Label enthaltene URLs vom Text, damit der Text als
 * reines Label gerendert und die URLs separat als Links ausgegeben werden
 * können. Es wird kein HTML erzeugt.
 *
 * Entfernt wird nur die URL selbst – samt umschließender Klammern, wie sie
 * in Gesellschaftsfragen üblich sind („Frage (https://…)"). Der übrige Text
 * bleibt bis auf gekürzte Leerzeichen unverändert.
 */
export const splitLabelUrls = (label: string): LabelWithLinks => {
  const matches = label.match(URL_PATTERN) ?? [];

  if (matches.length === 0) {
    return { text: label, links: [] };
  }

  const hrefs = [...new Set(matches.map(trimUnbalancedParentheses))];

  // längste URL zuerst entfernen, damit eine URL, die Präfix einer anderen
  // ist, keine Reste hinterlässt
  const text = [...hrefs]
    .sort((a, b) => b.length - a.length)
    .reduce((remaining, href) => {
      const escaped = escapeRegExp(href);

      // „Text (URL)." → „Text.": die Klammern samt vorangehendem Leerzeichen
      // gehören zur URL; eine nackte URL wird nur selbst entfernt
      return remaining.replace(
        new RegExp(`\\s*\\(\\s*${escaped}\\s*\\)|${escaped}`, 'g'),
        '',
      );
    }, label)
    .replace(/\s{2,}/g, ' ')
    .trim();

  return {
    text,
    links: hrefs.map((href) => ({ href, text: linkTextForUrl(href) })),
  };
};
